<script>
import { mapGetters } from 'vuex'
import { required, email } from 'vuelidate/lib/validators'

import Translatable from '@src/mixins/translatable.js'

export default {
  mixins: [Translatable],
  validations: {
    name: {
      required,
    },
    email: {
      email,
      required,
    },
    title: {},
    text: {
      required,
    },
    country: {
      required,
    },
  },
  props: {
    showModal: Boolean,
    attraction: Object,
  },
  data() {
    return {
      stars: 0,
      maxStars: 5,
      name: '',
      email: '',
      title: '',
      text: '',
      country: '',
      isOutside: false,
    }
  },
  computed: {
    ...mapGetters('translations', ['currentLanguage']),
    isValidate() {
      return this.name && this.email && this.text && this.country && this.stars
    },
    data() {
      const data = {
        userName: this.name,
        text: this.text,
        email: this.email,
        language: this.currentLanguage,
        rating: this.stars,
        date: new Date(),
        userPlace: this.country,
        title: this.title,
        status: 'New',
        destinationId: process.env.VUE_APP_DESTINATION_ID,
      }

      if (this.attraction) {
        data.attractionId = this.attraction._id
      } else {
        data.attractionId = '0'
      }

      return data
    },
  },
  methods: {
    rate(star) {
      this.stars = star
    },
    onClick() {
      this.$emit('toggle', 'showModal')
    },
    handleFocusOut() {
      this.isOutside = true
    },
    sendReview() {
      if (this.$v.$invalid || !this.stars) {
        this.isOutside = true
        this.$v.$touch()
      } else {
        this.$store.dispatch('reviews/sendReview', this.data)
        this.onClick()
        this.$emit('toggle', 'showFeedback')

        this.name = ''
        this.email = ''
        this.title = ''
        this.text = ''
        this.country = ''
        this.stars = 0
        this.$v.$reset()
      }
    },
  },
}
</script>

<template>
  <div class="app">
    <div class="modal-background" @click="onClick"></div>
    <div class="modal">
      <div class="cross" @click="onClick"></div>
      <div class="description-container">
        <p
          class="description-title"
          v-html="getTranslatedContent('REVIEWS_review_form_title')"
        ></p>
        <p
          class="description-text"
          v-html="getTranslatedContent('REVIEWS_review_form_text')"
        ></p>
      </div>
      <div class="main-container">
        <div class="inputs">
          <div class="input-container">
            <label for="name" class="input-title"
              >{{ getTranslatedContent('REVIEWS_form_name_field') }}
              <p
                v-if="$v.name.$error && !$v.name.required"
                class="error-message"
                >{{ getTranslatedContent('FORM_validate_required') }}</p
              >
              <input
                id="name"
                v-model="$v.name.$model"
                type="text"
                :placeholder="
                  getTranslatedContent('REVIEWS_review_form_name_placeholder')
                "
                :class="$v.name.$error ? 'error' : ''"
              />
            </label>
          </div>
          <div class="input-container">
            <label for="email" class="input-title"
              >{{ getTranslatedContent('REVIEWS_review_form_email_field') }}
              <p
                v-if="$v.email.$error && !$v.email.required"
                class="error-message"
                >{{ getTranslatedContent('FORM_validate_required') }}</p
              >
              <p v-if="!$v.email.email && isOutside" class="error-message">{{
                getTranslatedContent('FORM_validate_email')
              }}</p>
              <input
                id="email"
                v-model="$v.email.$model"
                type="email"
                :placeholder="
                  getTranslatedContent('REVIEWS_review_form_email_placeholder')
                "
                :class="$v.email.$error && isOutside ? 'error' : ''"
                @focusout="handleFocusOut"
              />
            </label>
          </div>
          <div class="input-container">
            <label for="review" class="input-title"
              >{{ getTranslatedContent('REVIEWS_review_form_title_field') }}
              <p
                v-if="$v.title.$error && !$v.title.required"
                class="error-message"
                >{{ getTranslatedContent('FORM_validate_required') }}</p
              >
              <input
                id="review"
                v-model="$v.title.$model"
                type="text"
                :placeholder="
                  getTranslatedContent('REVIEWS_review_form_title_placeholder')
                "
                :class="$v.title.$error ? 'error' : ''"
              />
            </label>
          </div>
          <div class="input-container">
            <label for="city" class="input-title"
              >{{ getTranslatedContent('REVIEWS_review_form_city_field') }}
              <p
                v-if="$v.country.$error && !$v.country.required"
                class="error-message"
                >{{ getTranslatedContent('FORM_validate_required') }}</p
              >
              <input
                id="city"
                v-model="$v.country.$model"
                type="text"
                :placeholder="
                  getTranslatedContent('REVIEWS_review_form_city_placeholder')
                "
                :class="$v.country.$error ? 'error' : ''"
              />
            </label>
          </div>
        </div>
        <div class="rating-container">
          <p class="input-title">{{
            getTranslatedContent('REVIEWS_review_form_rating_field')
          }}</p>
          <div class="rating">
            <ul
              v-for="star in maxStars"
              :key="star"
              class="star-btn-mixin"
              @click="rate(star)"
            >
              <li
                :class="star <= stars ? 'star-btn-active' : 'star-btn-inactive'"
              >
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="input-review-container">
        <textarea
          v-model="$v.text.$model"
          class="input-review"
          cols="30"
          rows="10"
          :placeholder="
            getTranslatedContent('REVIEWS_review_form_text_placeholder')
          "
          :class="$v.text.$error ? 'error' : ''"
        >
        </textarea>
      </div>
      <div class="button-send-container">
        <button
          class="button-send"
          :class="isValidate ? '' : 'disabled'"
          @click="sendReview"
        >
          {{ getTranslatedContent('REVIEWS_send_btn') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';
.app {
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  z-index: 98;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.modal-background {
  background-color: rgba(0, 0, 0, 0.3);
  width: 100%;
  height: 100%;
}

.modal {
  background-color: $white;
  width: 755px;
  height: auto;
  border-radius: 20px;
  position: absolute;
  z-index: 100;
  padding: 0 50px 25px 50px;
}

.cross {
  cursor: pointer;
  position: absolute;
  width: 24px;
  height: 24px;
  top: 20px;
  right: 20px;
  background: url('../../assets/icons/cross-modal.svg') center no-repeat;
}

.description-container {
  width: 100%;
  margin: 23px 0 23px 0;
}

.description-title {
  font-family: Open Sans Bold, sans-serif;
  font-style: normal;
  font-weight: bold;
  text-transform: uppercase;
  font-size: 22px;
  line-height: 130%;
  color: $dark-blue;
  padding-bottom: 15px;
}

.description-text {
  font-family: Open Sans, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 165%;
  color: $black;
  // opacity: 0.8;
}

.main-container {
  display: flex;
  flex-direction: column;
  margin-bottom: 5px;
}

.inputs {
  display: flex;
  flex-wrap: wrap;
}

.input-container:nth-child(2n) {
  margin-right: 0;
}

.input-container {
  width: 367px;
  height: 76px;
  margin: 0 20px 15px 0;
}

.input-container input {
  box-sizing: border-box;
  width: 100%;
  height: 48px;
  border: 1px solid $dark-gray;
  padding: 15px;
  margin-top: 10px;
  background: $white;
  font-family: Open Sans, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  color: rgba(37, 44, 62, 0.6);
}

.input-review-container {
  width: 100%;
  height: 128px;
}

.input-title {
  font-family: Open Sans Bold, sans-serif;
  font-weight: bold;
  font-style: normal;
  text-transform: uppercase;
  font-size: 14px;
  line-height: 130%;
  color: $dark-blue;
}

::placeholder {
  font-family: Open Sans, sans-serif;
  font-style: normal;
  font-weight: normal;
  line-height: 130%;
  color: rgba(37, 44, 62, 0.6);
}

.input-review {
  box-sizing: border-box;
  height: 100%;
  width: 100%;
  border: 1px solid $dark-gray;
  padding: 15px;
  background: $white;
  font-family: Open Sans, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  color: rgba(37, 44, 62, 0.6);
  resize: none;
}

.rating-container {
  min-width: 250px;
  display: flex;
  flex-direction: row;
  align-items: center;
  // margin: 10px 0;
}

.rating-container .input-title {
  margin-right: 15px;
}

.button-send-container {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin: 24px 0 0 0;
}

.button-send {
  width: 245px;
  height: 48px;
  background: $dark-blue;
  border-radius: 5px;
  border: 1px solid $dark-blue;
  text-transform: uppercase;
  font-family: Open Sans Bold, sans-serif;
  font-style: normal;
  font-weight: bold;
  font-size: 15px;
  line-height: 130%;
  color: $white;

  &:hover {
    border: 1px solid $hover-blue;
    background: $hover-blue;
  }

  &.disabled {
    background: $gray;
    border: none;
    opacity: 0.4;
  }
}

.star-btn-mixin {
  cursor: pointer;
  display: inline-block;
  width: 40px;
  height: 40px;
  margin: auto 5px 5px auto;
}

.star-btn-inactive {
  @extend .star-btn-mixin;
  background: url('../../assets/icons/star-modal-inactive.svg') center no-repeat;
}

.star-btn-active {
  @extend .star-btn-mixin;
  background: url('../../assets/icons/star-modal-active.svg') center no-repeat;
}

.error-message {
  display: inline;
  font-family: Open Sans Semibold;
  font-style: normal;
  font-size: 12px;
  line-height: 16px;
  text-transform: lowercase;
  color: $orange;
  margin-left: 10px;
}

.error {
  border: 1px solid $orange !important;
}

.description-title,
.description-text {
  :deep(p:empty) {
    height: 1em;
    margin: 0;
  }

  :deep(p:has(br:last-child)::after) {
    content: '';
    display: block;
    height: 1em;
  }
}

@media only screen and (max-width: $container-width-xs) {
  .app {
    z-index: 130;
  }
  .modal {
    padding: 0 8px 0px 8px;
    border-radius: 0;
    width: auto;
    height: 100vh;
    overflow-y: scroll;
  }

  .main-container {
    display: flex;
    flex-direction: column;
  }

  .description-container {
    width: 100%;
    margin: 40px 0 16px 0;
  }

  .description-text {
    opacity: 1;
  }

  .input-container {
    width: 100%;
    margin: 16px 0 0 0;
  }

  .input-title {
    font-family: Open Sans Semibold, sans-serif;
    font-weight: 600;
  }

  .input-review-container {
    width: 100%;
    height: 120px;
  }

  .button-send {
    width: 100%;
  }

  .button-send-container {
    padding-bottom: 30px;
  }

  .rating-container {
    flex-direction: column;
    margin: 16px 0 20px 3px;
    align-items: flex-start;
  }

  .star-btn-mixin {
    margin: 5px 5px 5px 0;
  }

  .cross {
    top: 39px;
    right: 8px;
  }
}
</style>
